import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { createRoot } from 'react-dom/client';
import L from 'leaflet';
import * as turf from '@turf/turf';
import type { GridParams } from 'contexts/GridContext';
import { lv95ToWgs84ForOneCoordinate, wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';
import RotateControlContent from './CadastralRotateControl';
import {
  getLayerMap,
  getLayerOuterRing,
  layerRingToLv95,
  type BorderType,
  type Lv95Coordinate,
  type ManualRotateSession,
  type PointerCoordinates,
  type SetGridParams,
} from './cadastralShared';
import {
  getRotateControlLatLng,
  refreshEditHandles,
  rotatePolygonOuterRing,
  syncRotateControlAppearance,
  type RotateControlState,
} from './rotationControl';
import { buildTurfPolygonFromLatLngs, getCombinedAnalysisAreasCenter } from './cadastralGeometry';
import type { AddCenterMarker } from './AnalysisAreaCenterMarkerSync';

interface UseCadastralRotationOptions {
  legalBorderRef: { current: L.Polygon | null };
  buildableBorderRef: { current: L.Polygon | null };
  analysisAreaRef: { current: L.Polygon | null };
  centerOfAnalysisAreaRef: { current: L.Marker | null };
  rotateControlRef: { current: RotateControlState | null };
  rotateAngleDegreesRef: { current: number };
  manualRotateSessionRef: { current: ManualRotateSession | null };
  activeEditBorderRef: { current: BorderType | null };
  editDraftCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
  gridParams: GridParams;
  analysisAreas: Lv95Coordinate[][];
  setGridParams: SetGridParams;
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  addCenterMarker: AddCenterMarker;
  onCenterReset: () => void;
}

const useCadastralRotation = ({
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  centerOfAnalysisAreaRef,
  rotateControlRef,
  rotateAngleDegreesRef,
  manualRotateSessionRef,
  activeEditBorderRef,
  editDraftCoordinatesRef,
  gridParams,
  analysisAreas,
  setGridParams,
  setCoordinates,
  addCenterMarker,
  onCenterReset,
}: UseCadastralRotationOptions) => {
  const getLayerRefByType = (borderType: BorderType): L.Polygon | null => {
    switch (borderType) {
      case 'legalBorder':
        return legalBorderRef.current;
      case 'buildableBorder':
        return buildableBorderRef.current;
      case 'analysisArea':
        return analysisAreaRef.current;
      default:
        return null;
    }
  };

  const getBorderCoordinatesByType = (borderType: BorderType): Lv95Coordinate[] => {
    switch (borderType) {
      case 'legalBorder':
        return gridParams?.legalBorder || [];
      case 'buildableBorder':
        return gridParams?.buildableBorder || [];
      case 'analysisArea':
        return gridParams?.analysisArea || [];
      default:
        return [];
    }
  };

  const getNormalizedRotateAngle = useCallback(() => {
    const nextAngle = Number(rotateAngleDegreesRef.current);
    return Number.isFinite(nextAngle) ? nextAngle : 90;
  }, []);

  const getManualRotatePivot = useCallback(
    (borderType: BorderType, layerRef: L.Polygon): L.LatLng | null => {
      if (borderType === 'analysisArea') {
        const markerLatLng = centerOfAnalysisAreaRef.current?.getLatLng();
        if (markerLatLng) return markerLatLng;

        const manualCenterX = gridParams?.manualCenter?.x;
        const manualCenterY = gridParams?.manualCenter?.y;
        if (Number.isFinite(manualCenterX) && Number.isFinite(manualCenterY)) {
          const { lat, lng } = lv95ToWgs84ForOneCoordinate(Number(manualCenterX), Number(manualCenterY));
          return L.latLng(lat, lng);
        }
      }

      const polygon = buildTurfPolygonFromLatLngs(getLayerOuterRing(layerRef));
      if (!polygon) return null;
      const [lng, lat] = turf.center(polygon).geometry.coordinates;
      return L.latLng(lat, lng);
    },
    [gridParams?.manualCenter]
  );

  const getPointerAngleOnMap = useCallback((map: L.Map, pivotLatLng: L.LatLng, pointer: PointerCoordinates) => {
    const mapContainer = map.getContainer();
    const containerRect = mapContainer.getBoundingClientRect();
    const pointerX = pointer.clientX - containerRect.left;
    const pointerY = pointer.clientY - containerRect.top;
    const pivotPoint = map.latLngToContainerPoint(pivotLatLng);
    return Math.atan2(pointerY - pivotPoint.y, pointerX - pivotPoint.x);
  }, []);

  const startManualRotate = useCallback(
    (borderType: BorderType, pointer: PointerCoordinates) => {
      const layerRef = getLayerRefByType(borderType);
      const map = getLayerMap(layerRef);
      if (!layerRef || !map) return;

      const pivotLatLng = getManualRotatePivot(borderType, layerRef);
      const baseLatLngs = getLayerOuterRing(layerRef);
      if (!pivotLatLng || baseLatLngs.length < 3) return;

      manualRotateSessionRef.current = {
        borderType,
        layerRef,
        map,
        pivotLatLng,
        baseLatLngs,
        startPointerAngle: getPointerAngleOnMap(map, pivotLatLng, pointer),
        lastAngleDegrees: 0,
      };
    },
    [getManualRotatePivot, getPointerAngleOnMap]
  );

  const updateRotateControlPosition = useCallback(() => {
    const activeBorderType = activeEditBorderRef.current;
    const rotateControl = rotateControlRef.current;
    if (!activeBorderType || !rotateControl) return;

    const layerRef = getLayerRefByType(activeBorderType);
    const map = getLayerMap(layerRef);
    if (!layerRef || !map) return;

    const nextLatLng = getRotateControlLatLng(layerRef, map);
    if (nextLatLng) {
      syncRotateControlAppearance(map, rotateControl.marker, rotateControl.container);
      rotateControl.marker.setLatLng(nextLatLng);
    }
  }, []);

  const applyRotatedBorderLatLngs = useCallback(
    (
      borderType: BorderType,
      layerRef: L.Polygon,
      rotatedLatLngs: L.LatLng[],
      options: { refreshHandles?: boolean; updateControlPosition?: boolean; syncCoordinatesState?: boolean } = {}
    ) => {
      layerRef.setLatLngs(rotatedLatLngs);
      if (options.refreshHandles) refreshEditHandles(layerRef);

      const updatedLv95Coordinates = rotatedLatLngs.map((coord) => wgs84ToLv95ForOneCoordinate(coord.lng, coord.lat));
      editDraftCoordinatesRef.current[borderType] = updatedLv95Coordinates;
      if (options.syncCoordinatesState !== false) setCoordinates(updatedLv95Coordinates);
      if (options.updateControlPosition !== false) updateRotateControlPosition();
    },
    [updateRotateControlPosition]
  );

  const updateManualRotate = useCallback(
    (pointer: PointerCoordinates) => {
      const session = manualRotateSessionRef.current;
      if (!session) return;

      const currentAngle = getPointerAngleOnMap(session.map, session.pivotLatLng, pointer);
      const angleDeltaDegrees = ((((currentAngle - session.startPointerAngle) * 180) / Math.PI + 540) % 360) - 180;
      const rotatedLatLngs = rotatePolygonOuterRing(session.baseLatLngs, angleDeltaDegrees, {
        pivot: [session.pivotLatLng.lng, session.pivotLatLng.lat],
      });
      if (!rotatedLatLngs) return;

      session.lastAngleDegrees = angleDeltaDegrees;
      applyRotatedBorderLatLngs(session.borderType, session.layerRef, rotatedLatLngs, {
        syncCoordinatesState: false,
        updateControlPosition: false,
      });
    },
    [applyRotatedBorderLatLngs, getPointerAngleOnMap]
  );

  const stopManualRotate = useCallback(() => {
    const session = manualRotateSessionRef.current;
    if (!session) return;
    if (Math.abs(session.lastAngleDegrees) > 0.0001) {
      setCoordinates(layerRingToLv95(session.layerRef));
      refreshEditHandles(session.layerRef);
      updateRotateControlPosition();
    }
    manualRotateSessionRef.current = null;
  }, [updateRotateControlPosition]);

  const removeRotateControl = useCallback(() => {
    stopManualRotate();
    rotateControlRef.current?.cleanup();
    rotateControlRef.current = null;
  }, [stopManualRotate]);

  const rotateBorderByAngle = useCallback(
    (borderType: BorderType) => {
      const layerRef = getLayerRefByType(borderType);
      if (!layerRef) return;
      const latLngs = getLayerOuterRing(layerRef);
      const pivotLatLng = getManualRotatePivot(borderType, layerRef);
      const rotatedLatLngs = rotatePolygonOuterRing(latLngs, getNormalizedRotateAngle(), {
        pivot: pivotLatLng ? [pivotLatLng.lng, pivotLatLng.lat] : undefined,
      });
      if (!rotatedLatLngs) return;
      applyRotatedBorderLatLngs(borderType, layerRef, rotatedLatLngs, { refreshHandles: true });
    },
    [applyRotatedBorderLatLngs, getManualRotatePivot, getNormalizedRotateAngle]
  );

  const renderRotateControl = useCallback(
    (borderType: BorderType) => {
      const rotateControl = rotateControlRef.current;
      if (!rotateControl?.mounted) return;

      rotateControl.root.render(
        <RotateControlContent
          initialAngle={getNormalizedRotateAngle()}
          canResetCenter={borderType === 'analysisArea'}
          onRotate={() => rotateBorderByAngle(borderType)}
          onResetCenter={() => {
            const analysisLayer = analysisAreaRef.current;
            const map = getLayerMap(analysisLayer);
            const polygon = analysisLayer ? buildTurfPolygonFromLatLngs(getLayerOuterRing(analysisLayer)) : null;
            const combinedCenter = getCombinedAnalysisAreasCenter(analysisAreas);
            if (!analysisLayer || !map || !polygon) return;
            addCenterMarker(polygon, map, setGridParams, combinedCenter ? [combinedCenter.x, combinedCenter.y] : null);
            onCenterReset();
          }}
          onAngleChange={(nextValue) => {
            rotateAngleDegreesRef.current = nextValue;
          }}
          onManualRotateStart={(pointer) => startManualRotate(borderType, pointer)}
          onManualRotateMove={updateManualRotate}
          onManualRotateEnd={stopManualRotate}
        />
      );
    },
    [getNormalizedRotateAngle, rotateBorderByAngle, startManualRotate, stopManualRotate, updateManualRotate]
  );

  const ensureRotateControl = useCallback(
    (borderType: BorderType) => {
      const layerRef = getLayerRefByType(borderType);
      const map = getLayerMap(layerRef);
      if (!layerRef || !map) return false;

      const markerLatLng = getRotateControlLatLng(layerRef, map);
      if (!markerLatLng) return false;

      const existingControl = rotateControlRef.current;
      if (existingControl?.mounted) {
        existingControl.marker.addTo(map);
        syncRotateControlAppearance(map, existingControl.marker, existingControl.container);
        existingControl.marker.setLatLng(markerLatLng);
        renderRotateControl(borderType);
        return true;
      }

      const container = document.createElement('div');
      const root = createRoot(container);
      const marker = L.marker(markerLatLng, {
        interactive: true,
        keyboard: false,
        zIndexOffset: 1000,
        icon: L.divIcon({
          className: 'grid-rotate-control-marker',
          html: container,
          iconSize: [44, 44],
          iconAnchor: [22, 22],
        }),
      }).addTo(map);

      syncRotateControlAppearance(map, marker, container);
      const rotateControlState: RotateControlState = {
        marker,
        root,
        container,
        mounted: true,
        cleanup: () => {
          rotateControlState.mounted = false;
          root.unmount();
          getLayerMap(marker)?.removeLayer(marker);
        },
      };
      rotateControlRef.current = rotateControlState;
      renderRotateControl(borderType);
      return true;
    },
    [renderRotateControl]
  );

  return {
    getLayerRefByType,
    getBorderCoordinatesByType,
    updateRotateControlPosition,
    removeRotateControl,
    renderRotateControl,
    ensureRotateControl,
  };
};

export default useCadastralRotation;
